"use client";

import { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18next, { restoreStoredLanguage } from '../i18n/client';

/**
 * i18n bootstrap with hydration-safe language switching.
 *
 * Two protections layered together:
 *
 * 1. Defensive reset on first mount: if `i18next.language` is somehow not the initial
 *    language (`en`), force it back via useState's lazy initializer (which runs exactly
 *    once on first render of this component, before children render). This guards
 *    against module-load side effects, browser-extension interference, or Turbopack
 *    HMR state leakage that might have left i18next in a non-default state.
 *
 * 2. Deferred restore of the visitor's saved language: the server HTML is always English,
 *    so the first client render must be too. Once the page has hydrated, an effect asks
 *    restoreStoredLanguage() to switch to the language saved by the switcher (it waits for
 *    an idle moment so lazily hydrated Suspense boundaries are not caught mid-switch, and its
 *    bundle was already being fetched while hydration ran). Nothing else applies the saved
 *    language: without this effect a reload silently drops back to English.
 */
export default function I18nProvider({
  children,
  initialLanguage
}: {
  children: React.ReactNode,
  initialLanguage: string
}) {
  // Lazy initializer runs exactly once, on first render only. Side effect is safe here.
  const [, _setInitialized] = useState(() => {
    if (typeof window !== 'undefined') {
      if (i18next.language !== initialLanguage) {
        i18next.changeLanguage(initialLanguage);
      }
    } else {
      // On the server, we might be serving multiple requests, but since 
      // Next.js App Router server components are async, we must rely on 
      // the synchronous call to changeLanguage during the render.
      // Since resources are pre-loaded in client.ts, this is instantaneous.
      if (i18next.language !== initialLanguage) {
        i18next.changeLanguage(initialLanguage);
      }
    }
    return true;
  });
  // Mark as intentionally unused; the useState exists solely for its initializer side effect.
  void _setInitialized;

  // Keep the <html lang> attribute in sync with the active language on the client.
  // The server always emits the default language (the root layout is static so
  // marketing pages can be edge-cached), so this is the only place the document
  // language gets corrected for non-default locales — important for a11y/SEO.
  useEffect(() => {
    const applyLang = () => {
      document.documentElement.lang = i18next.language || initialLanguage;
    };
    applyLang();
    i18next.on('languageChanged', applyLang);
    return () => {
      i18next.off('languageChanged', applyLang);
    };
  }, [initialLanguage]);

  useEffect(() => restoreStoredLanguage(), []);

  return (
    <I18nextProvider i18n={i18next} defaultNS={"translation"}>
      {children}
    </I18nextProvider>
  );
}
